import { ERROR_REGISTRY, type ErrorCode, isErrorCode } from '@repo/errors/registry';

type WireErrorBody = {
  error: {
    code: string;
    message: string;
    action?: string;
    details?: Record<string, unknown>;
    requestId: string;
  };
};

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly httpStatus: number;
  readonly action?: string;
  readonly details?: Record<string, unknown>;
  readonly requestId: string;

  constructor(params: {
    code: ErrorCode;
    message: string;
    httpStatus: number;
    action?: string;
    details?: Record<string, unknown>;
    requestId: string;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.httpStatus = params.httpStatus;
    this.action = params.action;
    this.details = params.details;
    this.requestId = params.requestId;
  }
}

function isWireErrorBody(value: unknown): value is WireErrorBody {
  if (!value || typeof value !== 'object') return false;
  if (!('error' in value)) return false;
  const inner: unknown = value.error;
  if (!inner || typeof inner !== 'object') return false;
  return 'code' in inner && 'message' in inner && 'requestId' in inner;
}

export async function parseApiError(response: Response): Promise<ApiError> {
  const requestIdHeader = response.headers.get('x-request-id') ?? 'unknown';
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return new ApiError({
      code: 'INTERNAL_ERROR',
      message: ERROR_REGISTRY.INTERNAL_ERROR.defaultMessage,
      action: ERROR_REGISTRY.INTERNAL_ERROR.defaultAction,
      httpStatus: response.status,
      requestId: requestIdHeader,
    });
  }

  if (isWireErrorBody(body)) {
    const code: ErrorCode = isErrorCode(body.error.code) ? body.error.code : 'INTERNAL_ERROR';
    return new ApiError({
      code,
      message: body.error.message,
      action: body.error.action,
      details: body.error.details,
      httpStatus: response.status,
      requestId: body.error.requestId || requestIdHeader,
    });
  }

  return new ApiError({
    code: 'INTERNAL_ERROR',
    message: ERROR_REGISTRY.INTERNAL_ERROR.defaultMessage,
    action: ERROR_REGISTRY.INTERNAL_ERROR.defaultAction,
    httpStatus: response.status,
    requestId: requestIdHeader,
  });
}

export function networkError(): ApiError {
  return new ApiError({
    code: 'NETWORK_ERROR',
    message: ERROR_REGISTRY.NETWORK_ERROR.defaultMessage,
    action: ERROR_REGISTRY.NETWORK_ERROR.defaultAction,
    httpStatus: 0,
    requestId: 'unknown',
  });
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
